<script lang="ts" setup>
import { reactive, computed } from "vue";
import BreadCrumb from "@/components/Common/BreadCrumb.vue";
import Notificacao from "@/components/Notificacao/Index.vue";
import * as service from "@/services/BoletosService";
import { fdata, f2dec } from "@/utils/funcoes";

const controle = reactive({
  arquivo: null as File | null,
  itens: [] as service.ImpRelBancoItemDTO[],
  selecionados: {} as Record<number, boolean>,
  importado: false,
  resultadoAplicacao: [] as string[],
  status: "",
  erro: "",
});

const totalSelecionados = computed(() => Object.values(controle.selecionados).filter(v => v).length);

function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  controle.arquivo = target.files && target.files.length > 0 ? target.files[0] : null;
}

function importar() {
  if (!controle.arquivo) {
    controle.erro = "Selecione um arquivo!";
    return;
  }

  controle.status = "Importando...";
  controle.erro = "";
  controle.resultadoAplicacao = [];

  service.importarRelatorioBanco(controle.arquivo)
    .then((r) => {
      controle.itens = r;
      controle.selecionados = {};
      r.forEach((item, idx) => {
        controle.selecionados[idx] = item.podeAplicar;
      });
      controle.importado = true;
      controle.status = "";

      if (r.length === 0) {
        controle.erro = "Nenhuma linha encontrada no arquivo.";
      }
    })
    .catch((erro) => {
      controle.erro = erro.message || erro;
      controle.status = "";
    });
}

function marcarTodos(valor: boolean) {
  controle.itens.forEach((item, idx) => {
    if (item.podeAplicar) controle.selecionados[idx] = valor;
  });
}

function aplicar() {
  const escolhidos = controle.itens
    .map((item, idx) => ({ item, idx }))
    .filter(({ item, idx }) => controle.selecionados[idx] && item.podeAplicar && item.boletoId != null)
    .map(({ item }) => ({
      boletoId: item.boletoId as number,
      tipo: item.tipo,
      valor: item.valor,
      valorCobrado: item.valorCobrado,
      dataLiquidacao: item.dataLiquidacao,
    } as service.ImpRelBancoAplicarDTO));

  if (escolhidos.length === 0) {
    controle.erro = "Nenhum item selecionado!";
    return;
  }

  controle.status = `Aplicando ${escolhidos.length} item(ns)...`;
  controle.erro = "";

  service.aplicarRelatorioBanco(escolhidos)
    .then((r) => {
      controle.resultadoAplicacao = r;
      controle.status = "";
      controle.itens = [];
      controle.importado = false;
      controle.arquivo = null;
    })
    .catch((erro) => {
      controle.erro = erro.message || erro;
      controle.status = "";
    });
}

function cancelar() {
  controle.importado = false;
  controle.itens = [];
  controle.selecionados = {};
}

function descricaoTipo(tipo: string | undefined) {
  if (tipo === "Liquidados") return "Liquidados (pagos)";
  if (tipo === "Baixados") return "Baixados";
  if (tipo === "Vencidos") return "Vencidos (informativo)";
  return tipo ?? "";
}
</script>

<template>
  <BreadCrumb PageTitle="Importar Relatório do Banco" :items="[
    { label: 'Cobrança', to: '/boletos' },
    { label: 'Importar Relatório do Banco' }]" />

  <Notificacao :erro="controle.erro" @limparErro="controle.erro = ''" :progresso="controle.status"
    @limparProgresso="controle.status = ''" />

  <div class="card mb-2" v-if="!controle.importado">
    <div class="card-body">
      <p class="text-muted">
        Importe a planilha (.xlsx) de Títulos exportada do portal do banco — Vencidos, Liquidados ou Baixados.
        O tipo do relatório é detectado automaticamente pelas colunas do arquivo. Depois de importar, você confere
        e escolhe quais boletos terão a ação aplicada antes de confirmar.
      </p>
      <div class="row mb-2">
        <div class="col-md-6">
          <label>Arquivo (.xlsx)</label>
          <input type="file" accept=".xlsx" class="form-control form-control-sm" @change="onFileChange" />
        </div>
      </div>
      <a class="btn btn-primary btn-sm" @click="importar()">
        <i class="fa fa-upload me-1"></i> Importar
      </a>
    </div>
  </div>

  <div class="card" v-if="controle.importado">
    <div class="card-header d-flex justify-content-between align-items-center flex-wrap">
      <span class="fw-bold">
        {{ controle.itens.length }} registro(s) — {{ descricaoTipo(controle.itens[0]?.tipo) }}
      </span>
      <div>
        <a class="btn btn-sm btn-outline-secondary me-1" href="#" @click.prevent="marcarTodos(true)">Marcar todos</a>
        <a class="btn btn-sm btn-outline-secondary" href="#" @click.prevent="marcarTodos(false)">Desmarcar todos</a>
      </div>
    </div>
    <div class="card-body p-0">
      <div class="table-responsive">
        <table class="table table-sm table-striped table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th class="text-center">Aplicar?</th>
              <th>Nosso Número</th>
              <th>Pagador</th>
              <th class="text-end">Valor</th>
              <th class="text-nowrap">Vencimento</th>
              <th>Ação</th>
              <th>Status Atual</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="controle.itens.length == 0">
              <td colspan="7" class="text-center">Nenhum registro encontrado</td>
            </tr>
            <tr v-for="(item, idx) in controle.itens" :key="idx" :class="{ 'table-secondary': !item.podeAplicar }">
              <td class="text-center">
                <input type="checkbox" v-model="controle.selecionados[idx]" :disabled="!item.podeAplicar" />
              </td>
              <td>{{ item.nossoNumero }}</td>
              <td>{{ item.pagador }}</td>
              <td class="text-end text-nowrap">{{ f2dec(item.tipo == 'Liquidados' ? (item.valorCobrado ?? item.valor) : item.valor) }}</td>
              <td class="text-nowrap">{{ fdata(item.dataVencimento) }}</td>
              <td>
                <span :class="item.boletoId == null ? 'text-danger' : (item.podeAplicar ? 'text-primary fw-bold' : 'text-muted')">
                  {{ item.acaoProposta }}
                </span>
              </td>
              <td>{{ item.statusAtualDescricao ?? '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="card-footer">
      <div class="row">
        <div class="col-md-12">
          <a class="btn btn-sm btn-success me-1" href="#" @click.prevent="aplicar()">
            <i class="fa fa-check me-1"></i> Aplicar Selecionados ({{ totalSelecionados }})
          </a>
          <a class="btn btn-sm btn-secondary" href="#" @click.prevent="cancelar()">
            Cancelar
          </a>
        </div>
      </div>
    </div>
  </div>

  <div class="card mt-2" v-if="controle.resultadoAplicacao.length">
    <div class="card-header fw-bold">Resultado da aplicação</div>
    <div class="card-body">
      <ul class="mb-0">
        <li v-for="(linha, idx) in controle.resultadoAplicacao" :key="idx">{{ linha }}</li>
      </ul>
    </div>
  </div>
</template>
